import type { FC, ReactNode } from 'react'
import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react'

import { Layers, RotateCcw, ScanSearch } from 'lucide-react'
// A-Frame must define `window.AFRAME` before react-force-graph pulls in
// aframe-extras (the VR variant), which registers components at module load.
import 'aframe'
import { ForceGraph2D, ForceGraph3D } from 'react-force-graph'
import SpriteText from 'three-spritetext'
import * as THREE from 'three'

import { useStore } from '@nanostores/react'

import { Checkbox } from '~/elements/Checkbox'
import { Tab, Tabs, TabsList } from '~/elements/Tabs'
import { IconButton } from '~/elements/IconButton'
import { Menu, MenuTitle } from '~/elements/Menu'
import type { ResolvedTheme } from '~/features/auth/stores/theme'
import { $resolvedTheme } from '~/features/auth/stores/theme'

export type GraphMode = '2d' | '3d'
export type GraphNodeKind = 'record' | 'property'
export type GraphLinkKind = 'record-relation' | 'property-value'

export type GraphNode = {
  id: string
  kind: GraphNodeKind
  label: string
  color: string
  recordId?: string
  propertyName?: string
  propertyType?: string
  propertyLabels?: string[]
  vectorIndexed?: boolean
  connectedRecordIds?: string[]
}

export type GraphLink = {
  id: string
  kind: GraphLinkKind
  source: string
  target: string
  relationType?: string
  // Assigned at render time so parallel/bidirectional links fan out as arcs
  // instead of overlapping on a single straight line.
  curvature?: number
}

export type GraphOutput = {
  nodes: GraphNode[]
  links: GraphLink[]
}

type HoverHighlights = {
  nodeIds: Set<string>
  linkIds: Set<string>
}

// Layer toggles surfaced in the "Graph Layers" menu. A consumer lists only the
// toggles relevant to its data (e.g. the ontology view has no property nodes),
// and the rest stay fixed at their default value.
export type GraphLayerKey =
  | 'properties'
  | 'propertyLinks'
  | 'recordLinks'
  | 'recordLabels'
  | 'relationshipTypes'

const ALL_LAYERS: GraphLayerKey[] = [
  'properties',
  'propertyLinks',
  'recordLinks',
  'recordLabels',
  'relationshipTypes'
]

function LayerToggle({
  checked,
  description,
  label,
  onCheckedChange
}: {
  checked: boolean
  description: string
  label: string
  onCheckedChange: () => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <Checkbox checked={checked} className="mt-1" onCheckedChange={onCheckedChange} />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium text-content">{label}</span>
        <span className="text-sm leading-snug text-content2">{description}</span>
      </span>
    </label>
  )
}

function getGraphRefId(ref: unknown): string | undefined {
  if (typeof ref === 'string') return ref
  if (ref && typeof ref === 'object' && 'id' in (ref as Record<string, unknown>)) {
    const value = (ref as { id?: unknown }).id
    return typeof value === 'string' ? value : undefined
  }
  return undefined
}

// Spread links that share the same pair of endpoints into separate bézier arcs so
// multiple relationship types (and bidirectional edges) don't stack on one straight
// line. Mutates the `curvature` of each link in place. Mirrors the canonical
// react-force-graph multigraph recipe.
function assignParallelLinkCurvatures(links: GraphLink[]) {
  const MAX_CURVATURE = 0.5
  const groups = new Map<string, GraphLink[]>()

  links.forEach((link) => {
    const pairId =
      link.source <= link.target ? `${link.source} ${link.target}` : `${link.target} ${link.source}`
    const group = groups.get(pairId) ?? []
    group.push(link)
    groups.set(pairId, group)
  })

  groups.forEach((group) => {
    // Self-loops always need a positive curvature to render as a visible loop.
    if (group[0].source === group[0].target) {
      group.forEach((link, index) => {
        link.curvature = 0.3 + index * 0.25
      })
      return
    }

    if (group.length === 1) {
      group[0].curvature = 0
      return
    }

    const lastIndex = group.length - 1
    const lastLink = group[lastIndex]
    lastLink.curvature = MAX_CURVATURE
    const delta = (2 * MAX_CURVATURE) / lastIndex
    for (let index = 0; index < lastIndex; index++) {
      // Flip arcs for links pointing the opposite way so they bow consistently.
      const flip = lastLink.source !== group[index].source ? -1 : 1
      group[index].curvature = (-MAX_CURVATURE + index * delta) * flip
    }
  })
}

function drawSquare(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
  const half = size / 2
  ctx.beginPath()
  ctx.rect(x - half, y - half, size, size)
  ctx.closePath()
}

// Theme-aware colors for the canvas/WebGL renderers. These can't use Tailwind
// CSS variables (they're passed to react-force-graph as plain color strings),
// so we resolve a palette from the active theme instead.
export type GraphPalette = {
  background: string
  vectorBadge: string
  // Subtle ring drawn around every 2D node so low-contrast fills (e.g. a white
  // label color on a light background) stay visible and delineated.
  nodeStroke: string
  // 2D links — RGB triplets composed with a computed opacity at render time.
  link2dRecordRGB: string
  link2dPropertyRGB: string
  link2dRecordIdle: string
  link2dPropertyIdle: string
  arrow2dHighlight: string
  arrow2dIdle: string
  // 2D labels (passed through withOpacity).
  recordLabelHi: string
  recordLabelLo: string
  relTypeHi: string
  relTypeLo: string
  // 3D links / labels.
  link3dRecordHighlight: string
  link3dPropertyHighlight: string
  link3dRecordIdle: string
  link3dPropertyIdle: string
  arrow3dHighlight: string
  arrow3dIdle: string
  label3dRecord: string
  label3dRelType: string
  // Hover tooltip shell (inline style string).
  tooltipShellStyle: string
}

const DARK_GRAPH_PALETTE: GraphPalette = {
  background: '#222',
  vectorBadge: '#10b981',
  nodeStroke: 'rgba(255, 255, 255, 0.14)',
  link2dRecordRGB: '216,226,236',
  link2dPropertyRGB: '172,205,23',
  link2dRecordIdle: 'rgba(145,155,166,0.14)',
  link2dPropertyIdle: 'rgba(172,205,23,0.07)',
  arrow2dHighlight: 'rgba(216,226,236,0.88)',
  arrow2dIdle: 'rgba(145,155,166,0.2)',
  recordLabelHi: '#d9e2ec',
  recordLabelLo: '#9aa7b8',
  relTypeHi: '#d9e2ec',
  relTypeLo: '#9aa7b8',
  link3dRecordHighlight: '#d8e0e8',
  link3dPropertyHighlight: '#86909b',
  link3dRecordIdle: '#303841',
  link3dPropertyIdle: '#262d34',
  arrow3dHighlight: '#d8e0e8',
  arrow3dIdle: '#303841',
  label3dRecord: 'rgba(205, 215, 226, 0.88)',
  label3dRelType: '#d9e2ec',
  tooltipShellStyle:
    'background: rgba(0, 0, 0, 0.5); color: #fff; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 8px; padding: 8px 10px; line-height: 1.35; font-size: 12px; backdrop-filter: blur(2px); max-width: 320px;'
}

const LIGHT_GRAPH_PALETTE: GraphPalette = {
  background: '#fafafa',
  vectorBadge: '#10b981',
  nodeStroke: 'rgba(15, 23, 42, 0.35)',
  link2dRecordRGB: '71,85,105',
  link2dPropertyRGB: '120,144,16',
  link2dRecordIdle: 'rgba(71,85,105,0.22)',
  link2dPropertyIdle: 'rgba(120,144,16,0.2)',
  arrow2dHighlight: 'rgba(71,85,105,0.9)',
  arrow2dIdle: 'rgba(100,116,139,0.35)',
  recordLabelHi: '#1f2937',
  recordLabelLo: '#475569',
  relTypeHi: '#1f2937',
  relTypeLo: '#64748b',
  link3dRecordHighlight: '#475569',
  link3dPropertyHighlight: '#84909b',
  link3dRecordIdle: '#cbd5e1',
  link3dPropertyIdle: '#dde3ea',
  arrow3dHighlight: '#475569',
  arrow3dIdle: '#cbd5e1',
  label3dRecord: 'rgba(30, 41, 59, 0.9)',
  label3dRelType: '#1f2937',
  tooltipShellStyle:
    'background: rgba(255, 255, 255, 0.94); color: #1f2937; border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 8px; padding: 8px 10px; line-height: 1.35; font-size: 12px; backdrop-filter: blur(2px); box-shadow: 0 4px 16px rgba(0,0,0,0.12); max-width: 320px;'
}

const getGraphPalette = (theme: ResolvedTheme): GraphPalette =>
  theme === 'light' ? LIGHT_GRAPH_PALETTE : DARK_GRAPH_PALETTE

function withOpacity(color: string, opacity: number): string {
  if (!color) {
    return `rgba(255,255,255,${opacity})`
  }

  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[^)]+\)/, `rgba($1,$2,$3,${opacity})`)
  }

  if (color.startsWith('rgb(')) {
    const inside = color.slice(4, -1)
    return `rgba(${inside}, ${opacity})`
  }

  if (color.startsWith('#')) {
    let hex = color.slice(1)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((ch) => `${ch}${ch}`)
        .join('')
    }

    if (hex.length === 6) {
      const r = Number.parseInt(hex.slice(0, 2), 16)
      const g = Number.parseInt(hex.slice(2, 4), 16)
      const b = Number.parseInt(hex.slice(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }
  }

  return color
}

function mixNumber(from: number, to: number, amount: number): number {
  return from + (to - from) * amount
}

function configureReadableSprite(label: SpriteText) {
  label.renderOrder = 10
  label.material.depthTest = false
  label.material.depthWrite = false
  label.material.transparent = true
}

function defaultNodeHoverLabel(node: GraphNode, palette: GraphPalette): string {
  const shellStyle = palette.tooltipShellStyle

  if (node.kind === 'record') {
    return `<div style="${shellStyle}"><div><b>Record</b>: ${node.label}</div><div>ID: ${node.recordId ?? 'N/A'}</div></div>`
  }

  const vectorState = node.vectorIndexed ? 'yes' : 'no'
  return `<div style="${shellStyle}"><div><b>Property</b>: ${node.propertyName ?? node.label}</div><div>Type: ${node.propertyType ?? 'string'}</div><div>Vector indexed: ${vectorState}</div></div>`
}

function renderLinkLabel2D({
  ctx,
  globalScale,
  highlighted,
  labelScale = 1,
  link,
  palette,
  visible = highlighted
}: {
  ctx: CanvasRenderingContext2D
  globalScale: number
  highlighted: boolean
  labelScale?: number
  link: GraphLink
  palette: GraphPalette
  visible?: boolean
}) {
  if (!visible || !link.relationType || link.kind !== 'record-relation') {
    return
  }

  const fontSize = Math.min(Math.max((8 * labelScale) / globalScale, 2.5), 11 * labelScale)
  const color = highlighted ? palette.relTypeHi : palette.relTypeLo
  drawTextAlongCurve(ctx, link.relationType, link, fontSize, color)
}

// Quadratic-bézier control point for a link, matching react-force-graph's
// curvature model (mid-point + curvature * perpendicular).
function linkCurveGeometry(link: GraphLink) {
  const source = link.source as unknown as { x?: number; y?: number }
  const target = link.target as unknown as { x?: number; y?: number }
  const sourceX = source.x ?? 0
  const sourceY = source.y ?? 0
  const targetX = target.x ?? 0
  const targetY = target.y ?? 0
  const curvature = link.curvature ?? 0
  const midX = (sourceX + targetX) / 2
  const midY = (sourceY + targetY) / 2
  const controlX = midX + curvature * (targetY - sourceY)
  const controlY = midY + curvature * (sourceX - targetX)
  return { controlX, controlY, sourceX, sourceY, targetX, targetY }
}

// Draw a relationship label as glyphs bent along the link's bézier curve. Falls
// back to a straight midpoint label when the text is longer than the arc.
function drawTextAlongCurve(
  ctx: CanvasRenderingContext2D,
  text: string,
  link: GraphLink,
  fontSize: number,
  color: string
) {
  const { controlX, controlY, sourceX, sourceY, targetX, targetY } = linkCurveGeometry(link)

  const pointAt = (t: number) => {
    const mt = 1 - t
    return {
      x: mt * mt * sourceX + 2 * mt * t * controlX + t * t * targetX,
      y: mt * mt * sourceY + 2 * mt * t * controlY + t * t * targetY
    }
  }
  const tangentAt = (t: number) =>
    Math.atan2(
      2 * (1 - t) * (controlY - sourceY) + 2 * t * (targetY - controlY),
      2 * (1 - t) * (controlX - sourceX) + 2 * t * (targetX - controlX)
    )

  ctx.save()
  ctx.font = `${fontSize}px monospace`
  ctx.fillStyle = color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  // Arc-length table so glyphs are spaced evenly along the (non-uniform) curve.
  const SAMPLES = 60
  const samples: Array<{ len: number; t: number }> = [{ len: 0, t: 0 }]
  let prev = pointAt(0)
  let total = 0
  for (let i = 1; i <= SAMPLES; i++) {
    const t = i / SAMPLES
    const p = pointAt(t)
    total += Math.hypot(p.x - prev.x, p.y - prev.y)
    samples.push({ len: total, t })
    prev = p
  }

  const textWidth = ctx.measureText(text).width

  // Too long to bend cleanly — keep it readable as a straight midpoint label.
  if (textWidth > total * 0.95) {
    const mid = pointAt(0.5)
    ctx.fillText(text, mid.x, mid.y)
    ctx.restore()
    return
  }

  const tForLength = (s: number) => {
    const clamped = Math.max(0, Math.min(total, s))
    let i = 1
    while (i < samples.length && samples[i].len < clamped) i++
    const a = samples[i - 1]
    const b = samples[i] ?? a
    const span = b.len - a.len || 1
    return a.t + (b.t - a.t) * ((clamped - a.len) / span)
  }

  // Keep glyphs upright: if the curve runs right-to-left at its midpoint, walk
  // the path the other way and flip each glyph so the word still reads L→R.
  const flip = Math.cos(tangentAt(0.5)) < 0
  const startLen = total / 2 - textWidth / 2
  const perp = (flip ? 0.6 : -0.6) * fontSize
  let cursor = 0

  for (const ch of Array.from(text)) {
    const w = ctx.measureText(ch).width
    const sNonFlip = startLen + cursor + w / 2
    const s = flip ? total - sNonFlip : sNonFlip
    const t = tForLength(s)
    const p = pointAt(t)
    ctx.save()
    ctx.translate(p.x, p.y)
    ctx.rotate(tangentAt(t) + (flip ? Math.PI : 0))
    ctx.fillText(ch, 0, perp)
    ctx.restore()
    cursor += w
  }

  ctx.restore()
}

export type GraphCanvasProps = {
  /** Full graph data; the canvas applies layer visibility filtering internally. */
  graphData: GraphOutput
  /** Layer toggles available in the Graph Layers menu. Defaults to all of them. */
  availableLayers?: GraphLayerKey[]
  /** Called after the camera focuses a clicked node. */
  onNodeClick?: (node: GraphNode, event?: MouseEvent) => void
  /** Called when a link (edge) is clicked. */
  onLinkClick?: (link: GraphLink, event?: MouseEvent) => void
  /** HTML string shown on node hover. Defaults to a Record/Property tooltip. */
  getNodeHoverLabel?: (node: GraphNode) => string
  /** value for the container's data-tour attribute. */
  dataTour?: string
  /** When true, auto-fits the graph once the layout settles (used by the product tour). */
  tourFitActive?: boolean
  /** Absolutely-positioned overlays rendered inside the graph container. */
  children?: ReactNode
}

export const GraphCanvas: FC<GraphCanvasProps> = ({
  graphData,
  availableLayers = ALL_LAYERS,
  onNodeClick,
  onLinkClick,
  getNodeHoverLabel,
  dataTour,
  tourFitActive = false,
  children
}) => {
  const theme = useStore($resolvedTheme)
  const palette = useMemo(() => getGraphPalette(theme), [theme])
  const nodeHoverLabel = useCallback(
    (node: GraphNode) => (getNodeHoverLabel ? getNodeHoverLabel(node) : defaultNodeHoverLabel(node, palette)),
    [getNodeHoverLabel, palette]
  )
  const fgRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const pinned2DPositionsRef = useRef<Record<string, { x: number; y: number }>>({})
  const pinned3DPositionsRef = useRef<Record<string, { x: number; y: number; z: number }>>({})
  const draggingNodeIdRef = useRef<string | undefined>(undefined)
  // Highlighted-link set mirrored into a ref so the 3D label objects can read the
  // latest hover state without changing identity (which would force react-force-graph
  // to rebuild — and leak — every link's THREE object on each hover frame).
  const highlightedLinkIdsRef = useRef<Set<string>>(new Set())
  // Reused per-link 3D relationship-type sprites, created once and cached so hovering
  // never reallocates GPU textures.
  const linkSpritesRef = useRef<Map<string, SpriteText>>(new Map())

  const layerAvailable = useCallback((key: GraphLayerKey) => availableLayers.includes(key), [availableLayers])

  const [graphMode, setGraphMode] = useState<GraphMode>('2d')
  const [showProperties, setShowProperties] = useState(false)
  const [showPropertyLinks, setShowPropertyLinks] = useState(true)
  const [showRecordLinks, setShowRecordLinks] = useState(true)
  const [showRecordLabels, setShowRecordLabels] = useState(true)
  const [showRelationshipTypes, setShowRelationshipTypes] = useState(true)
  const [hoveredNodeId, setHoveredNodeId] = useState<string | undefined>(undefined)
  const [hoveredLinkId, setHoveredLinkId] = useState<string | undefined>(undefined)
  const hoverIntensityRef = useRef(0)
  const [hoverIntensity, setHoverIntensity] = useState(0)

  const visibleGraphData = useMemo(() => {
    const visibleNodes = graphData.nodes.filter((node) => {
      if (!showProperties && node.kind === 'property') return false
      return true
    })
    const visibleNodeIds = new Set(visibleNodes.map((node) => node.id))

    const visibleLinks = graphData.links
      .map((link) => {
        const sourceId = getGraphRefId((link as unknown as { source?: unknown }).source)
        const targetId = getGraphRefId((link as unknown as { target?: unknown }).target)
        if (!sourceId || !targetId) return undefined

        return {
          ...link,
          source: sourceId,
          target: targetId
        } satisfies GraphLink
      })
      .filter((link): link is GraphLink => !!link)
      .filter((link) => {
        if (!visibleNodeIds.has(link.source) || !visibleNodeIds.has(link.target)) return false
        if (!showRecordLinks && link.kind === 'record-relation') return false
        if (!showPropertyLinks && link.kind === 'property-value') return false
        return true
      })

    assignParallelLinkCurvatures(visibleLinks)

    return {
      nodes: visibleNodes,
      links: visibleLinks
    }
  }, [graphData.links, graphData.nodes, showProperties, showPropertyLinks, showRecordLinks])

  const hoverHighlights = useMemo<HoverHighlights>(() => {
    const highlightedNodeIds = new Set<string>()
    const highlightedLinkIds = new Set<string>()

    if (!hoveredNodeId && !hoveredLinkId) {
      return {
        nodeIds: highlightedNodeIds,
        linkIds: highlightedLinkIds
      }
    }

    const linksByNodeId = new Map<string, GraphLink[]>()
    visibleGraphData.links.forEach((link) => {
      const source = getGraphRefId((link as unknown as { source?: unknown }).source)
      const target = getGraphRefId((link as unknown as { target?: unknown }).target)
      if (!source || !target) return

      const sourceLinks = linksByNodeId.get(source) ?? []
      sourceLinks.push(link)
      linksByNodeId.set(source, sourceLinks)

      const targetLinks = linksByNodeId.get(target) ?? []
      targetLinks.push(link)
      linksByNodeId.set(target, targetLinks)
    })

    if (hoveredNodeId) {
      highlightedNodeIds.add(hoveredNodeId)

      const connected = linksByNodeId.get(hoveredNodeId) ?? []
      connected.forEach((link) => {
        highlightedLinkIds.add(link.id)
        const source = getGraphRefId((link as unknown as { source?: unknown }).source)
        const target = getGraphRefId((link as unknown as { target?: unknown }).target)
        if (source) highlightedNodeIds.add(source)
        if (target) highlightedNodeIds.add(target)
      })
    }

    if (hoveredLinkId) {
      // Hovering a single relationship highlights only that edge and its two
      // endpoints — not every other edge touching those nodes.
      const hoveredLink = visibleGraphData.links.find((link) => link.id === hoveredLinkId)
      if (hoveredLink) {
        highlightedLinkIds.add(hoveredLink.id)

        const source = getGraphRefId((hoveredLink as unknown as { source?: unknown }).source)
        const target = getGraphRefId((hoveredLink as unknown as { target?: unknown }).target)

        if (source) highlightedNodeIds.add(source)
        if (target) highlightedNodeIds.add(target)
      }
    }

    return {
      nodeIds: highlightedNodeIds,
      linkIds: highlightedLinkIds
    }
  }, [hoveredLinkId, hoveredNodeId, visibleGraphData.links])

  const hasHoverSelection = hoveredNodeId !== undefined || hoveredLinkId !== undefined

  const isNodeHighlighted = useCallback(
    (nodeId: string) => hasHoverSelection && hoverHighlights.nodeIds.has(nodeId),
    [hasHoverSelection, hoverHighlights.nodeIds]
  )

  const isLinkHighlighted = useCallback(
    (linkId: string) => hasHoverSelection && hoverHighlights.linkIds.has(linkId),
    [hasHoverSelection, hoverHighlights.linkIds]
  )

  // Keep the ref in sync so the (stable-identity) 3D link objects can read the
  // current highlight set per frame without triggering object rebuilds.
  useEffect(() => {
    highlightedLinkIdsRef.current = hasHoverSelection ? hoverHighlights.linkIds : new Set()
  }, [hasHoverSelection, hoverHighlights.linkIds])

  useEffect(() => {
    const target = hasHoverSelection ? 1 : 0
    let frame = 0

    const tick = () => {
      const current = hoverIntensityRef.current
      const next = mixNumber(current, target, 0.22)

      if (Math.abs(next - target) < 0.02) {
        hoverIntensityRef.current = target
        setHoverIntensity(target)
        return
      }

      hoverIntensityRef.current = next
      setHoverIntensity(next)
      frame = window.requestAnimationFrame(tick)
    }

    frame = window.requestAnimationFrame(tick)

    return () => {
      window.cancelAnimationFrame(frame)
    }
  }, [hasHoverSelection])

  useEffect(() => {
    if (draggingNodeIdRef.current) {
      return
    }

    if (graphMode === '2d') {
      // Apply saved 2D pinned positions when available.
      visibleGraphData.nodes.forEach((node) => {
        const pinned = pinned2DPositionsRef.current[node.id]
        const mutable = node as any
        if (pinned) {
          mutable.fx = pinned.x
          mutable.fy = pinned.y
        } else {
          mutable.fx = undefined
          mutable.fy = undefined
        }
        mutable.fz = undefined
      })
      return
    }

    // Re-apply saved 3D pinned positions after data/mode changes.
    visibleGraphData.nodes.forEach((node) => {
      const pinned = pinned3DPositionsRef.current[node.id]
      const mutable = node as any
      if (pinned) {
        mutable.fx = pinned.x
        mutable.fy = pinned.y
        mutable.fz = pinned.z
      } else {
        mutable.fx = undefined
        mutable.fy = undefined
        mutable.fz = undefined
      }
    })
  }, [graphMode, visibleGraphData])

  const focusNode = useCallback(
    (node: any) => {
      // ForceGraph3D supports cameraPosition, while ForceGraph2D uses centerAt/zoom.
      if (graphMode === '2d') {
        fgRef.current?.centerAt?.(node.x ?? 0, node.y ?? 0, 700)
        fgRef.current?.zoom?.(3.2, 700)
        return
      }

      const distance = 260
      const safeDistance = Math.max(1, Math.hypot(node.x ?? 0, node.y ?? 0, node.z ?? 0))
      const distRatio = 1 + distance / safeDistance

      fgRef.current?.cameraPosition?.(
        { x: (node.x ?? 0) * distRatio, y: (node.y ?? 0) * distRatio, z: (node.z ?? 0) * distRatio },
        node,
        1000
      )
    },
    [graphMode]
  )

  const handleClick = useCallback(
    (node: GraphNode, event?: MouseEvent) => {
      focusNode(node)
      onNodeClick?.(node, event)
    },
    [focusNode, onNodeClick]
  )

  const focusLink3D = useCallback((link: any) => {
    const distance = 70

    const sx = link.source.x ?? 0
    const sy = link.source.y ?? 0
    const sz = link.source.z ?? 0

    const tx = link.target.x ?? 0
    const ty = link.target.y ?? 0
    const tz = link.target.z ?? 0

    const dx = sx - tx
    const dy = sy - ty
    const dz = sz - tz

    const rdx = dz
    const rdy = dy
    const rdz = -dx

    const dist = Math.max(1, Math.hypot(rdx, rdy, rdz))

    const distRatio = 1 + distance / dist

    const mx = (sx + tx) / 2
    const my = (sy + ty) / 2
    const mz = (sz + tz) / 2

    const cx = mx + rdx * distRatio
    const cy = my + rdy * distRatio
    const cz = mz + rdz * distRatio

    fgRef.current?.cameraPosition({ x: cx, y: cy, z: cz }, { x: mx, y: my, z: mz }, 1000)
  }, [])

  const handleLinkClick3D = useCallback(
    (link: any, event?: MouseEvent) => {
      focusLink3D(link)
      onLinkClick?.(link as GraphLink, event)
    },
    [focusLink3D, onLinkClick]
  )

  const handleLinkClick2D = useCallback(
    (link: any, event?: MouseEvent) => {
      onLinkClick?.(link as GraphLink, event)
    },
    [onLinkClick]
  )

  const [canvasSize, setCanvasSize] = useState({
    width: 1,
    height: 1
  })

  useEffect(() => {
    const element = containerRef.current
    if (!element) {
      return
    }

    const resize = () => {
      const rect = element.getBoundingClientRect()
      setCanvasSize({
        width: Math.max(1, Math.floor(rect.width)),
        height: Math.max(1, Math.floor(rect.height))
      })
    }

    resize()

    const observer = new ResizeObserver(resize)
    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [])

  const fitGraph = useCallback(() => {
    if (graphMode !== '3d') {
      fgRef.current?.zoomToFit?.(400, 30)
      return
    }

    const positionedNodes = visibleGraphData.nodes
      .map((node) => {
        const mutable = node as any
        return {
          x: Number(mutable.x),
          y: Number(mutable.y),
          z: Number(mutable.z)
        }
      })
      .filter((node) => Number.isFinite(node.x) && Number.isFinite(node.y) && Number.isFinite(node.z))

    if (!positionedNodes.length) {
      fgRef.current?.zoomToFit?.(400, 30)
      return
    }

    const center = positionedNodes.reduce(
      (acc, node) => ({
        x: acc.x + node.x / positionedNodes.length,
        y: acc.y + node.y / positionedNodes.length,
        z: acc.z + node.z / positionedNodes.length
      }),
      { x: 0, y: 0, z: 0 }
    )
    const distances = positionedNodes
      .map((node) => Math.hypot(node.x - center.x, node.y - center.y, node.z - center.z))
      .sort((a, b) => a - b)
    const mainClusterRadius =
      distances[Math.min(distances.length - 1, Math.floor(distances.length * 0.9))] ?? 80
    const distance = Math.min(900, Math.max(120, mainClusterRadius * 2.4))
    const camera = fgRef.current?.camera?.()
    const direction = new THREE.Vector3(
      camera?.position?.x ?? 0,
      camera?.position?.y ?? 0,
      camera?.position?.z ?? 1
    )
      .sub(new THREE.Vector3(center.x, center.y, center.z))
      .normalize()

    if (!Number.isFinite(direction.x) || !Number.isFinite(direction.y) || !Number.isFinite(direction.z)) {
      direction.set(0, 0, 1)
    }

    fgRef.current?.cameraPosition?.(
      {
        x: center.x + direction.x * distance,
        y: center.y + direction.y * distance,
        z: center.z + direction.z * distance
      },
      center,
      500
    )
  }, [graphMode, visibleGraphData.nodes])

  const pendingTourFitRef = useRef(false)
  const pendingDataFitRef = useRef(false)

  // Fit the view automatically once the force layout settles after data loads —
  // the default camera leaves a freshly imported graph tiny and off-center.
  useEffect(() => {
    if (!visibleGraphData.nodes.length || canvasSize.width <= 1 || canvasSize.height <= 1) {
      return
    }

    pendingDataFitRef.current = true

    const fallback = window.setTimeout(() => {
      if (pendingDataFitRef.current) {
        pendingDataFitRef.current = false
        fitGraph()
      }
    }, 900)

    return () => window.clearTimeout(fallback)
  }, [
    canvasSize.height,
    canvasSize.width,
    fitGraph,
    graphMode,
    visibleGraphData.links.length,
    visibleGraphData.nodes.length
  ])

  useEffect(() => {
    if (!tourFitActive) {
      return
    }
    pendingTourFitRef.current = true
    // Fallback in case the simulation settled before this step became active
    // (onEngineStop only fires when the engine transitions to stopped).
    const fallback = window.setTimeout(() => {
      if (pendingTourFitRef.current) {
        pendingTourFitRef.current = false
        fitGraph()
      }
    }, 2500)
    return () => window.clearTimeout(fallback)
  }, [tourFitActive, fitGraph])

  const handleEngineStop = useCallback(() => {
    if (pendingDataFitRef.current) {
      pendingDataFitRef.current = false
      fitGraph()
    }

    if (pendingTourFitRef.current) {
      pendingTourFitRef.current = false
      fitGraph()
    }
  }, [fitGraph])

  const reheatSimulation = useCallback(() => {
    // Relayout should start from a clean simulation state with no pinned nodes.
    pinned2DPositionsRef.current = {}
    pinned3DPositionsRef.current = {}

    visibleGraphData.nodes.forEach((node) => {
      const mutable = node as any
      mutable.fx = undefined
      mutable.fy = undefined
      mutable.fz = undefined
    })

    fgRef.current?.d3ReheatSimulation?.()
  }, [visibleGraphData.nodes])

  const createNodeObject3D = useCallback(
    (node: GraphNode) => {
      const size = 9

      if (node.kind === 'property') {
        return new THREE.Mesh(
          new THREE.BoxGeometry(size, size, size),
          new THREE.MeshLambertMaterial({ color: node.color })
        )
      }

      const group = new THREE.Group()

      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(size / 2, 20, 20),
        new THREE.MeshLambertMaterial({ color: node.color })
      )
      group.add(sphere)

      if (showRecordLabels) {
        // Keep record labels visible in 3D with a dimmed color to reduce visual noise.
        const label = new SpriteText(node.label)
        label.color = palette.label3dRecord
        label.textHeight = 1.08
        label.position.set(0, -7.4, 0)
        configureReadableSprite(label)
        group.add(label)
      }

      return group
    },
    [showRecordLabels, palette]
  )

  // Stable identity (only depends on showRelationshipTypes) so react-force-graph
  // builds each link's sprite once. Sprites are cached and reused; visibility is
  // toggled per frame in updateLinkObjectPosition3D. This avoids reallocating (and
  // leaking) canvas textures on every hover.
  const createLinkObject3D = useCallback(
    (link: GraphLink) => {
      if (!showRelationshipTypes || !link.relationType || link.kind !== 'record-relation') {
        return undefined
      }

      let label = linkSpritesRef.current.get(link.id)
      if (!label) {
        label = new SpriteText(link.relationType)
        label.color = palette.label3dRelType
        label.textHeight = 1.15
        configureReadableSprite(label)
        linkSpritesRef.current.set(link.id, label)
      }
      label.visible = highlightedLinkIdsRef.current.has(link.id)
      return label
    },
    [showRelationshipTypes, palette]
  )

  const updateLinkObjectPosition3D = useCallback(
    (object: THREE.Object3D | undefined, { start, end }: any, link?: GraphLink) => {
      if (!object) {
        return false
      }

      // Refresh visibility/scale from the live hover state without rebuilding objects.
      if (link?.id) {
        const highlighted = highlightedLinkIdsRef.current.has(link.id)
        object.visible = highlighted
        if (highlighted && object instanceof SpriteText) {
          object.textHeight = 1.15 + hoverIntensityRef.current * 0.2
        }
      }

      object.position.x = start.x + (end.x - start.x) * 0.52
      object.position.y = start.y + (end.y - start.y) * 0.52
      object.position.z = start.z + (end.z - start.z) * 0.52
      return true
    },
    []
  )

  // Dispose cached relationship sprites for links that no longer exist (and all of
  // them on unmount) so GPU textures don't accumulate across data reloads.
  useEffect(() => {
    const validIds = new Set(graphData.links.map((link) => link.id))
    const cache = linkSpritesRef.current
    cache.forEach((sprite, id) => {
      if (!validIds.has(id)) {
        const material = sprite.material as THREE.SpriteMaterial | undefined
        material?.map?.dispose()
        material?.dispose()
        cache.delete(id)
      }
    })
  }, [graphData.links])

  useEffect(() => {
    const cache = linkSpritesRef.current
    return () => {
      cache.forEach((sprite) => {
        const material = sprite.material as THREE.SpriteMaterial | undefined
        material?.map?.dispose()
        material?.dispose()
      })
      cache.clear()
    }
  }, [])

  const renderNode2D = useCallback(
    (node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const x = (node as any).x ?? 0
      const y = (node as any).y ?? 0
      const radius = 5
      const highlighted = isNodeHighlighted(node.id)
      const size = radius + (highlighted ? hoverIntensity * 1.2 : 0)

      ctx.save()
      ctx.fillStyle = node.color

      if (node.kind === 'property') {
        drawSquare(ctx, x, y, size * 2)
        ctx.fill()
      } else {
        ctx.beginPath()
        ctx.arc(x, y, size, 0, Math.PI * 2)
        ctx.closePath()
        ctx.fill()
      }

      // Outline the node so low-contrast fills stay visible (e.g. a white label
      // color on the light theme's near-white background).
      ctx.lineWidth = 0.75
      ctx.strokeStyle = palette.nodeStroke
      ctx.stroke()

      // vector badge
      if (node.kind === 'property' && node.vectorIndexed) {
        ctx.beginPath()
        ctx.fillStyle = palette.vectorBadge
        ctx.arc(x - size + 0.5, y - size + 0.5, 1.6, 0, Math.PI * 2)
        ctx.fill()
      }

      if (node.kind === 'record' && showRecordLabels) {
        const fontSize = Math.max(8 / globalScale, 2.5)
        ctx.font = `${fontSize}px monospace`
        ctx.fillStyle =
          highlighted ? withOpacity(palette.recordLabelHi, 0.75) : withOpacity(palette.recordLabelLo, 0.38)
        ctx.textAlign = 'center'
        ctx.fillText(node.label, x, y + 9)
      }
      ctx.restore()
    },
    [hoverIntensity, isNodeHighlighted, showRecordLabels, palette]
  )

  const renderNodePointerArea2D = useCallback(
    (node: GraphNode, color: string, ctx: CanvasRenderingContext2D) => {
      const x = (node as any).x ?? 0
      const y = (node as any).y ?? 0
      const radius = 6

      ctx.fillStyle = color
      if (node.kind === 'property') {
        drawSquare(ctx, x, y, radius * 2)
        ctx.fill()
        return
      }

      ctx.beginPath()
      ctx.arc(x, y, radius, 0, Math.PI * 2)
      ctx.closePath()
      ctx.fill()
    },
    []
  )

  const renderLink2D = useCallback(
    (link: GraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
      renderLinkLabel2D({
        ctx,
        globalScale,
        highlighted: isLinkHighlighted(link.id),
        labelScale: 1 + hoverIntensity * 0.14,
        link,
        palette,
        visible: showRelationshipTypes && isLinkHighlighted(link.id) && hoverIntensity > 0.12
      })
    },
    [hoverIntensity, isLinkHighlighted, showRelationshipTypes, palette]
  )

  // Runs after nodes are drawn: re-paint highlighted links (and their labels) on
  // top so a hovered relationship's path is never occluded by intervening nodes.
  const renderFramePost2D = useCallback(
    (ctx: CanvasRenderingContext2D, globalScale: number) => {
      // Re-draw only the relationship label on top so it isn't occluded by
      // nodes. The link line itself keeps force-graph's own (recolored) stroke.
      if (!showRelationshipTypes || hoverIntensity <= 0.12) return
      for (const link of visibleGraphData.links) {
        if (!isLinkHighlighted(link.id)) continue
        renderLinkLabel2D({
          ctx,
          globalScale,
          highlighted: true,
          labelScale: 1 + hoverIntensity * 0.14,
          link,
          palette,
          visible: true
        })
      }
    },
    [visibleGraphData.links, isLinkHighlighted, palette, hoverIntensity, showRelationshipTypes]
  )

  const showNodeSection = layerAvailable('properties')
  const showLinkSection = layerAvailable('propertyLinks') || layerAvailable('recordLinks')
  const showLabelSection = layerAvailable('recordLabels') || layerAvailable('relationshipTypes')

  const renderCommonGraphControls = (
    <>
      <div className="absolute top-4 left-4 z-20 flex flex-col gap-2">
        {(showNodeSection || showLinkSection || showLabelSection) && (
          <Menu
            align="start"
            className="min-w-[240px] p-2"
            modal={false}
            trigger={
              <IconButton
                aria-label="Graph layers"
                className="bg-fill/90 shadow-xl backdrop-blur-sm"
                size="small"
                variant="outline"
              >
                <Layers />
              </IconButton>
            }
          >
            <MenuTitle className="px-2 pt-1 pb-1 tracking-wide uppercase">Graph Layers</MenuTitle>
            <div className="flex flex-col gap-3 px-2 pb-2" onClick={(event) => event.stopPropagation()}>
              {showNodeSection && (
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-semibold text-content3 uppercase">Nodes</div>
                  <LayerToggle
                    checked={showProperties}
                    description="Show shared property nodes around records."
                    label="Property nodes"
                    onCheckedChange={() => setShowProperties((v) => !v)}
                  />
                </div>
              )}

              {showLinkSection && (
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-semibold text-content3 uppercase">Links</div>
                  {layerAvailable('propertyLinks') && (
                    <LayerToggle
                      checked={showPropertyLinks}
                      description="Show links between properties and records."
                      label="Property links"
                      onCheckedChange={() => setShowPropertyLinks((v) => !v)}
                    />
                  )}
                  {layerAvailable('recordLinks') && (
                    <LayerToggle
                      checked={showRecordLinks}
                      description="Show relationships between records."
                      label="Record relationships"
                      onCheckedChange={() => setShowRecordLinks((v) => !v)}
                    />
                  )}
                </div>
              )}

              {showLabelSection && (
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-semibold text-content3 uppercase">Labels</div>
                  {layerAvailable('recordLabels') && (
                    <LayerToggle
                      checked={showRecordLabels}
                      description="Show label names under record nodes."
                      label="Record labels"
                      onCheckedChange={() => setShowRecordLabels((v) => !v)}
                    />
                  )}
                  {layerAvailable('relationshipTypes') && (
                    <LayerToggle
                      checked={showRelationshipTypes}
                      description="Show relationship type names on record links."
                      label="Relationship types"
                      onCheckedChange={() => setShowRelationshipTypes((v) => !v)}
                    />
                  )}
                </div>
              )}
            </div>
          </Menu>
        )}
        <IconButton
          aria-label="Fit graph"
          className="bg-fill/90 shadow-xl backdrop-blur-sm"
          onClick={fitGraph}
          size="small"
          variant="outline"
        >
          <ScanSearch />
        </IconButton>
        <IconButton
          aria-label="Relayout graph"
          className="bg-fill/90 shadow-xl backdrop-blur-sm"
          onClick={reheatSimulation}
          size="small"
          variant="outline"
        >
          <RotateCcw />
        </IconButton>
      </div>
    </>
  )

  return (
    <div
      className="graph-view relative h-full min-h-0 overflow-hidden"
      data-tour={dataTour}
      ref={containerRef}
    >
      <style>{`
        .graph-view .graph-tooltip,
        .graph-tooltip {
          background: transparent !important;
          color: #fff !important;
          border: none !important;
          border-radius: 0;
          padding: 0 !important;
          line-height: 1.35;
          font-size: 12px;
          backdrop-filter: none;
          max-width: 320px;
          box-shadow: none !important;
        }
      `}</style>
      {renderCommonGraphControls}

      <Tabs
        className="absolute top-4 right-4 z-20"
        onValueChange={(value) => setGraphMode(value as GraphMode)}
        value={graphMode}
      >
        <TabsList>
          <Tab value="2d">2D</Tab>
          <Tab value="3d">3D</Tab>
        </TabsList>
      </Tabs>

      {children}

      {graphMode === '3d' ?
        <ForceGraph3D
          ref={fgRef}
          backgroundColor={palette.background}
          showNavInfo={false}
          graphData={visibleGraphData}
          linkWidth={() => {
            return 0
          }}
          linkOpacity={1}
          linkColor={(link: GraphLink) => {
            const highlighted = isLinkHighlighted(link.id)
            if (highlighted) {
              return link.kind === 'record-relation' ?
                  palette.link3dRecordHighlight
                : palette.link3dPropertyHighlight
            }
            return link.kind === 'record-relation' ? palette.link3dRecordIdle : palette.link3dPropertyIdle
          }}
          nodeOpacity={1}
          nodeRelSize={4.5}
          nodeId={'id'}
          linkSource={'source'}
          linkTarget={'target'}
          linkCurvature={(link: GraphLink) => link.curvature ?? 0}
          nodeResolution={24}
          height={canvasSize.height}
          width={canvasSize.width}
          nodeColor={(node: GraphNode) => node.color}
          linkDirectionalArrowRelPos={1}
          linkDirectionalArrowLength={(link: GraphLink) => (link.kind === 'record-relation' ? 1.15 : 0)}
          linkDirectionalArrowColor={(link: GraphLink) =>
            isLinkHighlighted(link.id) ? palette.arrow3dHighlight : palette.arrow3dIdle
          }
          linkDirectionalArrowResolution={12}
          nodeThreeObject={createNodeObject3D as any}
          linkThreeObject={createLinkObject3D as any}
          linkThreeObjectExtend
          linkPositionUpdate={updateLinkObjectPosition3D as any}
          onLinkClick={handleLinkClick3D}
          onNodeHover={(node: GraphNode | null) => {
            if (draggingNodeIdRef.current) {
              return
            }
            setHoveredLinkId(undefined)
            setHoveredNodeId(node?.id)
          }}
          onLinkHover={(link: GraphLink | null) => {
            if (draggingNodeIdRef.current) {
              return
            }
            setHoveredNodeId(undefined)
            setHoveredLinkId(link?.id)
          }}
          onNodeDrag={(node: any) => {
            draggingNodeIdRef.current = String(node.id ?? '')
            node.fx = node.x
            node.fy = node.y
            node.fz = node.z
          }}
          onNodeDragEnd={(node: any) => {
            node.fx = node.x
            node.fy = node.y
            node.fz = node.z
            draggingNodeIdRef.current = undefined

            if (node?.id && Number.isFinite(node.x) && Number.isFinite(node.y) && Number.isFinite(node.z)) {
              pinned3DPositionsRef.current[String(node.id)] = {
                x: node.x,
                y: node.y,
                z: node.z
              }
            }
          }}
          nodeLabel={nodeHoverLabel as any}
          onNodeClick={handleClick as any}
          onEngineStop={handleEngineStop}
        />
      : <ForceGraph2D
          ref={fgRef}
          backgroundColor={palette.background}
          graphData={visibleGraphData}
          onEngineStop={handleEngineStop}
          nodeId={'id'}
          linkSource={'source'}
          linkTarget={'target'}
          linkCurvature={(link: GraphLink) => link.curvature ?? 0}
          height={canvasSize.height}
          width={canvasSize.width}
          linkWidth={(link: GraphLink) => {
            const highlighted = isLinkHighlighted(link.id)
            const base = link.kind === 'record-relation' ? 0.72 : 0.34
            const active = link.kind === 'record-relation' ? 2.35 : 1.1
            return highlighted ? mixNumber(base, active, hoverIntensity) : base
          }}
          linkColor={(link: GraphLink) => {
            const highlighted = isLinkHighlighted(link.id)
            if (highlighted) {
              const opacity = mixNumber(
                link.kind === 'record-relation' ? 0.16 : 0.08,
                link.kind === 'record-relation' ? 0.82 : 0.46,
                hoverIntensity
              )
              return link.kind === 'record-relation' ?
                  `rgba(${palette.link2dRecordRGB},${opacity})`
                : `rgba(${palette.link2dPropertyRGB},${opacity})`
            }
            return link.kind === 'record-relation' ? palette.link2dRecordIdle : palette.link2dPropertyIdle
          }}
          nodeRelSize={5}
          linkDirectionalArrowRelPos={1}
          linkDirectionalArrowLength={(link: GraphLink) => (link.kind === 'record-relation' ? 2.2 : 0)}
          linkDirectionalArrowColor={(link: GraphLink) => {
            if (isLinkHighlighted(link.id)) {
              return palette.arrow2dHighlight
            }
            return palette.arrow2dIdle
          }}
          linkCanvasObjectMode={() => 'after'}
          linkCanvasObject={renderLink2D as any}
          onRenderFramePost={renderFramePost2D}
          nodeCanvasObject={renderNode2D as any}
          nodePointerAreaPaint={renderNodePointerArea2D as any}
          onLinkClick={handleLinkClick2D}
          cooldownTicks={520}
          onNodeHover={(node: GraphNode | null) => {
            if (draggingNodeIdRef.current) {
              return
            }
            setHoveredLinkId(undefined)
            setHoveredNodeId(node?.id)
          }}
          onLinkHover={(link: GraphLink | null) => {
            if (draggingNodeIdRef.current) {
              return
            }
            setHoveredNodeId(undefined)
            setHoveredLinkId(link?.id)
          }}
          onNodeDrag={(node: any) => {
            draggingNodeIdRef.current = String(node.id ?? '')
            node.fx = node.x
            node.fy = node.y
            node.fz = undefined
          }}
          onNodeDragEnd={(node: any) => {
            // 2D mode persists manually dragged positions.
            node.fx = node.x
            node.fy = node.y
            node.fz = undefined
            draggingNodeIdRef.current = undefined

            if (node?.id && Number.isFinite(node.x) && Number.isFinite(node.y)) {
              pinned2DPositionsRef.current[String(node.id)] = {
                x: node.x,
                y: node.y
              }
            }
          }}
          nodeLabel={nodeHoverLabel as any}
          onNodeClick={handleClick as any}
        />
      }
    </div>
  )
}
